import { PartnerForm } from '@/components/partner'
import { FormProvider, useForm } from 'react-hook-form'
import { useOutletContext } from 'react-router-dom'
import { Partner } from '@/types/partner'
import {
  PartnerFormData,
  partnerFormSchema,
  partnerDefaultValues,
} from '@/components/partner/partner-form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdatePartner } from '@/api/accounting-dealer'
import {
  cleanObject,
  formatDate,
  parseISOtoDate,
  sanitizeCnpj,
  toast,
} from '@/helpers'
import { LoadingButton } from '@mui/lab'
import { useCompany } from '@/context/companies'
import { useQueryClient } from '@tanstack/react-query'

export function PartnerDataPage() {
  const { company } = useCompany()

  const queryClient = useQueryClient()

  const { tax_id, pis, partnership, key, name } = useOutletContext<Partner>()

  const form = useForm<PartnerFormData>({
    resolver: zodResolver(partnerFormSchema),
    defaultValues: partnerDefaultValues,
    values: {
      name: name ?? '',
      tax_id: tax_id ?? '',
      admission_date: partnership?.admission_date
        ? parseISOtoDate(partnership.admission_date)
        : null,
      pis: pis ?? '',
      pro_labore_type: partnership?.pro_labore_type ?? '',
      has_double_bond: partnership?.has_double_bond ?? '',
      inss_base_amount: partnership?.inss_base_amount?.toString(),
      inss_contribution_amount:
        partnership?.inss_contribution_amount?.toString(),
      dual_social_security_indicator:
        partnership?.dual_social_security_indicator ?? '',
      second_cnpj_link: partnership?.second_cnpj_link ?? '',
      category_code: partnership?.category_code ?? '',
      pro_labore_amount: partnership?.pro_labore_amount?.toString(),
    },
  })

  const { mutate: updatePartner, isPending: isUpdatingPartner } =
    useUpdatePartner({
      onSuccess: () => {
        toast.success('Sócio atualizado com sucesso')
        queryClient.invalidateQueries({
          queryKey: ['partner', company.key, key],
        })
      },
    })

  const handleUpdatePartner = (data: PartnerFormData) => {
    updatePartner({
      companyKey: company.key,
      partnerKey: key,
      partnerData: {
        ...cleanObject({
          name: data.name,
          tax_id: data.tax_id,
          partnership: {
            pro_labore_type: data.pro_labore_type,
            admission_date: data.admission_date
              ? formatDate(data.admission_date, 'yyyy-MM-dd')
              : undefined,
            pro_labore_amount: data.pro_labore_amount
              ? Number(data.pro_labore_amount)
              : undefined,
            inss_base_amount: data.inss_base_amount
              ? Number(data.inss_base_amount)
              : undefined,
            inss_contribution_amount: data.inss_contribution_amount
              ? Number(data.inss_contribution_amount)
              : undefined,
            category_code: data.category_code,
            has_double_bond: Boolean(data.has_double_bond),
            dual_social_security_indicator: data.dual_social_security_indicator,
            second_cnpj_link: data.second_cnpj_link
              ? sanitizeCnpj(data.second_cnpj_link)
              : undefined,
          },
        }),
        pis: data.pis || null,
      },
    })
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleUpdatePartner)}>
        <PartnerForm isUpdate />

        <LoadingButton
          type="submit"
          sx={{ mt: 3 }}
          fullWidth
          loading={isUpdatingPartner}
        >
          Salvar
        </LoadingButton>
      </form>
    </FormProvider>
  )
}
